import { createAsyncThunk } from "@reduxjs/toolkit";
import { HttpService } from "../../modules/shared/services";
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import { GlobalService } from "../../modules/shared/services";
import i18n from "../../i18n";

// Interfaces for progress and reports data
export interface ClientProgressData {
  level: number;
  totalSpent: number;
  completedProjects: number;
  activeProjects: number;
  totalReviews: number;
  averageRating: number;
  memberSince: string;
  achievements: Array<{
    id: number;
    title: string;
    description: string;
    achieved: boolean;
    progress?: number;
  }>;
  recentMilestones: Array<{
    date: string;
    title: string;
    description: string;
  }>;
  levelProgress: number;
  nextLevelRequirements: string[];
}

export interface ProfessionalProgressData {
  earnings: {
    current: number;
    previous: number;
    percentageChange: number;
  };
  jobSuccess: {
    current: number;
    previous: number;
    percentageChange: number;
  };
  completionRate: {
    current: number;
    previous: number;
    percentageChange: number;
  };
  responseRate: {
    current: number;
    previous: number;
    percentageChange: number;
  };
  recentMilestones: Array<{
    title: string;
    date: string;
    icon: string;
  }>;
  skillProgress: Array<{
    skill: string;
    progress: number;
  }>;
  goals: Array<{
    title: string;
    current: number;
    target: number;
    progress: number;
  }>;
}

export interface ClientReportsData {
  serviceHistory: Array<{
    id: string;
    service: string;
    date: string;
    professional: string;
    status: string;
    cost: number;
  }>;
  financialOverview: {
    totalSpent: number;
    monthlyBreakdown: Array<{
      month: string;
      amount: number;
    }>;
  };
  performanceMetrics: {
    completedServices: number;
    avgRating: number;
    responseTime: string;
  };
  totalCount: number;
  hasNextPage: boolean;
  currentPage: number;
  totalPages: number;
}

export interface ProfessionalReportsData {
  earningsReport: Array<{
    month: string;
    earnings: number;
  }>;
  jobPerformance: Array<{
    metric: string;
    value: number;
    target: number;
  }>;
  clientFeedback: Array<{
    id: string;
    clientName: string;
    rating: number;
    comment: string;
    date: string;
  }>;
  skillAnalytics: Array<{
    skill: string;
    rating: number;
    reviews: number;
  }>;
}

// Client Progress Actions
export const getClientProgress = createAsyncThunk<ClientProgressData, { userId?: string } | undefined>(
  "user/getClientProgress",
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      
      const queryParams = new URLSearchParams();
      if (params.userId) queryParams.append("userId", params.userId);
      
      const queryString = queryParams.toString();
      const url = `api/v1/users/progress/client${queryString ? `?${queryString}` : ""}`;
      
      const response = await HttpService.get(url, true, {});
      
      dispatch(hideLoader());
      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      
      const errorMessage = error?.data?.message || "Failed to load client progress";
      // GlobalService.showToast("error", "Error", errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Professional Progress Actions
export const getProfessionalProgress = createAsyncThunk<ProfessionalProgressData, { userId?: string } | undefined>(
  "user/getProfessionalProgress",
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      
      const queryParams = new URLSearchParams();
      if (params.userId) queryParams.append("userId", params.userId);
      
      const queryString = queryParams.toString();
      const url = `api/v1/users/progress/professional${queryString ? `?${queryString}` : ""}`;
      
      const response = await HttpService.get(url, true, {});
      
      dispatch(hideLoader());
      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      
      const errorMessage = error?.data?.message || "Failed to load professional progress";
      GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Client Reports Actions
export const getClientReports = createAsyncThunk<
  ClientReportsData,
  { userId?: string; page?: number; limit?: number; dateRange?: string; serviceType?: string } | undefined
>(
  "user/getClientReports",
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      
      const queryParams = new URLSearchParams();
      if (params.userId) queryParams.append("userId", params.userId);
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.dateRange) queryParams.append("dateRange", params.dateRange);
      if (params.serviceType) queryParams.append("serviceType", params.serviceType);
      
      const queryString = queryParams.toString();
      const url = `api/v1/users/reports/client${queryString ? `?${queryString}` : ""}`;
      
      const response = await HttpService.get(url, true, {});
      
      dispatch(hideLoader());
      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      
      const errorMessage = error?.data?.message || "Failed to load client reports";
      // GlobalService.showToast("error", "Error", errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Professional Reports Actions
export const getProfessionalReports = createAsyncThunk<
  ProfessionalReportsData,
  { timeframe?: string }
>(
  "user/getProfessionalReports",
  async (filters = {}, { dispatch, rejectWithValue }) => {
    try {
      dispatch(showLoader());
      
      const params = new URLSearchParams();
      if (filters.timeframe) params.append("timeframe", filters.timeframe);
      
      const queryString = params.toString();
      const url = `api/v1/users/reports/professional${queryString ? `?${queryString}` : ""}`;
      
      const response = await HttpService.get(url, true, {});
      
      dispatch(hideLoader());
      return response;
    } catch (error: any) {
      dispatch(hideLoader());
      
      const errorMessage = error?.data?.message || "Failed to load professional reports";
      GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);
